/**
 * Type-aware Option editor for the admin Question form.
 *
 * Renders the right control set per `QuestionType`:
 *   - multiple / truefalse → single-correct radio
 *   - multi → multi-correct checkboxes
 *   - order → no correctness UI; only text + sort_order
 *   - short / filltext → text-list of accepted phrases (no is_correct)
 *   - fillnumber → exactly 1 row asking for `min|max`
 *
 * Per-type *validation* lives in the API (Module 2 `validateQuestionInput`);
 * this component is a thin authoring surface so admins can compose Options
 * without copy-pasting the wire shape.
 */

import { type ReactElement } from 'react';
import type { OptionInput, QuestionType } from '../../state/questions/api.ts';

export interface OptionEditorProps {
  type: QuestionType;
  options: OptionInput[];
  onChange: (next: OptionInput[]) => void;
}

function setAt<T>(arr: T[], idx: number, value: T): T[] {
  return arr.map((item, i) => (i === idx ? value : item));
}

export function OptionEditor(props: OptionEditorProps): ReactElement {
  const { type, options, onChange } = props;

  const addOption = (): void => {
    const next: OptionInput = {
      text: '',
      is_correct: false,
      sort_order: options.length + 1,
    };
    onChange([...options, next]);
  };

  const removeOption = (idx: number): void => {
    onChange(options.filter((_, i) => i !== idx));
  };

  const setText = (idx: number, text: string): void => {
    const opt = options[idx];
    if (!opt) return;
    onChange(setAt(options, idx, { ...opt, text }));
  };

  const setSort = (idx: number, sort_order: number): void => {
    const opt = options[idx];
    if (!opt) return;
    onChange(setAt(options, idx, { ...opt, sort_order }));
  };

  const setCorrectSingle = (idx: number): void => {
    onChange(
      options.map((o, i) => ({ ...o, is_correct: i === idx })),
    );
  };

  const toggleCorrectMulti = (idx: number): void => {
    const opt = options[idx];
    if (!opt) return;
    onChange(setAt(options, idx, { ...opt, is_correct: !opt.is_correct }));
  };

  if (type === 'fillnumber') {
    const opt = options[0];
    return (
      <div className="qb-option-editor qb-option-editor--fillnumber">
        <label>
          <span>Numeric range (min|max)</span>
          <input
            type="text"
            value={opt?.text ?? ''}
            placeholder="e.g. 1|10"
            onChange={(e) => {
              if (opt) setText(0, e.target.value);
              else onChange([{ text: e.target.value, sort_order: 1 }]);
            }}
          />
        </label>
      </div>
    );
  }

  if (type === 'short' || type === 'filltext') {
    return (
      <div className="qb-option-editor qb-option-editor--phrase">
        {options.map((opt, idx) => (
          <div key={idx} className="qb-option-row">
            <input
              type="text"
              value={opt.text}
              placeholder="Accepted phrase"
              onChange={(e) => setText(idx, e.target.value)}
            />
            <button type="button" onClick={() => removeOption(idx)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" onClick={addOption}>
          Add phrase
        </button>
      </div>
    );
  }

  if (type === 'order') {
    return (
      <div className="qb-option-editor qb-option-editor--order">
        {options.map((opt, idx) => (
          <div key={idx} className="qb-option-row">
            <input
              type="text"
              value={opt.text}
              onChange={(e) => setText(idx, e.target.value)}
            />
            <input
              type="number"
              min={1}
              value={opt.sort_order}
              onChange={(e) =>
                setSort(idx, Number.parseInt(e.target.value || '1', 10))
              }
              aria-label="sort order"
            />
            <button type="button" onClick={() => removeOption(idx)}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" onClick={addOption}>
          Add step
        </button>
      </div>
    );
  }

  // multiple / truefalse → single-correct; multi → multi-correct.
  const isMulti = type === 'multi';
  return (
    <div className="qb-option-editor qb-option-editor--choice">
      {options.map((opt, idx) => (
        <div key={idx} className="qb-option-row">
          {isMulti ? (
            <input
              type="checkbox"
              checked={opt.is_correct === true}
              onChange={() => toggleCorrectMulti(idx)}
              aria-label="is correct"
            />
          ) : (
            <input
              type="radio"
              name="qb-correct"
              checked={opt.is_correct === true}
              onChange={() => setCorrectSingle(idx)}
              aria-label="is correct"
            />
          )}
          <input
            type="text"
            value={opt.text}
            onChange={(e) => setText(idx, e.target.value)}
          />
          <input
            type="number"
            min={1}
            value={opt.sort_order}
            onChange={(e) =>
              setSort(idx, Number.parseInt(e.target.value || '1', 10))
            }
            aria-label="sort order"
          />
          {type !== 'truefalse' && (
            <button type="button" onClick={() => removeOption(idx)}>
              Remove
            </button>
          )}
        </div>
      ))}
      {type !== 'truefalse' && (
        <button type="button" onClick={addOption}>
          Add option
        </button>
      )}
    </div>
  );
}
